/**
 * Agent API Endpoints
 * Agent tool calls (query, enrich-place, etc.)
 */

import { http } from '@/lib/http'
import { env } from '@/lib/env'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'

function wsAgent(sub: string): string {
  return `/workspaces/${getActiveWorkspaceId()}/agent/tools${sub}`
}

/**
 * Agent query request
 */
export interface AgentQueryRequest {
  query: string
  types?: string[]
  conversationIds?: string[]
  limit?: number
}

/**
 * Agent query response
 */
export interface AgentQueryResponse {
  results: Array<{
    id: string
    type: string
    name: string
    score: number
    attributes?: Record<string, any>
  }>
  total?: number
}

/**
 * Enrich place request
 */
export interface EnrichPlaceRequest {
  objectId: string
  forceRefresh?: boolean
}

/**
 * Enrich place response
 */
export interface EnrichPlaceResponse {
  objectId: string
  enriched: boolean
  data?: {
    google_place_id?: string
    rating?: number
    price_level?: number
    photos?: string[]
    reviews?: Array<{
      author: string
      rating: number
      text: string
      time: string
    }>
    [key: string]: any
  }
}

/**
 * Agent-safe query
 * Searches user's data with agent authentication
 */
export async function agentQuery(
  authToken: string | null,
  request: AgentQueryRequest,
  toolSession?: string
) {
  const headers: Record<string, string> = {}
  if (env.agentKey) headers['X-Agent-Key'] = env.agentKey
  if (toolSession) headers['X-Tool-Session'] = toolSession

  const response = await http.post<AgentQueryResponse>(
    wsAgent('/query'),
    request,
    {
      authToken,
      headers,
    }
  )

  return response.data
}

/**
 * Enrich a place with Google Places data
 * Agent tool for enriching location objects
 */
export async function enrichPlace(
  authToken: string | null,
  request: EnrichPlaceRequest,
  toolSession?: string
) {
  const headers: Record<string, string> = {}
  if (env.agentKey) headers['X-Agent-Key'] = env.agentKey
  if (toolSession) headers['X-Tool-Session'] = toolSession

  const response = await http.post<EnrichPlaceResponse>(
    wsAgent('/enrich-place'),
    request,
    {
      authToken,
      headers,
    }
  )

  return response.data
}
